import React, { useCallback, useEffect, useState } from 'react';
import styles from './profile-photos.module.css';
import PhotoUpload from '../photo-upload/photo-upload';
import ConfirmModal from '../confirm-modal/confirm-modal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../services/store/hooks';
import {
  useDeleteProfilePhotoMutation,
  useSetMainPhotoMutation,
  useRotateProfilePhotoMutation,
  useUploadProfilePhotoMutation,
} from '../../services/store/api/userApi';
import PhotoCard from '../photo-card/photo-card';

interface Props {
  fromShow?: 'rega' | 'profile';
}

const ProfilePhotos: React.FC<Props> = ({ fromShow = 'rega' }) => {
  const { profile } = useAppSelector(store => store.user);
  const navigate = useNavigate();
  const location = useLocation();

  const [photoToDelete, setPhotoToDelete] = useState<number | null>(null);
  const [activePhotoId, setActivePhotoId] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [deletePhoto, { isLoading: isDeleting }] =
    useDeleteProfilePhotoMutation();
  const [setMainPhoto, { isLoading: isSettingMain }] =
    useSetMainPhotoMutation();
  const [rotatePhoto, { isLoading: isRotating }] =
    useRotateProfilePhotoMutation();
  const [uploadPhoto, { isLoading: isUploading }] =
    useUploadProfilePhotoMutation();

  const photosCount = profile?.photos?.length || 0;
  const uploadsToShow = Math.max(0, 3 - photosCount);
  const activePhotos = profile?.photos?.filter(photo => photo.status_moder === 'SUCCESS') || [];
  const activeMainPhotoId = activePhotos[0]?.id;

  const isAnyActionLoading = isDeleting || isRotating || isSettingMain || isUploading;

  useEffect(() => {
    if (photosCount > 0) {
      setValidationError(null);
    }
  }, [photosCount]);

  const handleConfirmDelete = async () => {
    if (!photoToDelete) return;

    try {
      await deletePhoto(photoToDelete).unwrap();
      setPhotoToDelete(null);
    } catch (err) {
      console.error('Ошибка при удалении фото', err);
    }
  };

  const handleSetMain = async (id: number) => {
    if (isAnyActionLoading) return;

    try {
      await setMainPhoto({ photo_id: id }).unwrap();
    } catch (err) {
      console.error('Ошибка при установке главного фото', err);
    }
  };

  const handleRotate = async (id: number) => {
    if (isAnyActionLoading) return;

    try {
      await rotatePhoto({ id, direction: 'right' }).unwrap();
    } catch (err) {
      console.error('Ошибка при повороте фото', err);
    }
  };

  const handleSuccessClick = useCallback(() => {
    if (photosCount < 1) {
      setValidationError('Загрузите хотя бы одно фото');
      return;
    }

    setValidationError(null);

    navigate('/profile-success', {
      state: { backgroundLocation: location },
    });
  }, [navigate, location, photosCount]);

  const handleReplace = async (photoId: number, file: File) => {
    if (isAnyActionLoading) return;

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('replace_photo_id', String(photoId));

    try {
      await uploadPhoto(formData).unwrap();
    } catch (err) {
      console.error('Ошибка при замене фото', err);
    }
  };

  const handleToggleActions = (photoId: number) => {
    setActivePhotoId(current => current === photoId ? null : photoId);
  };

  if (!profile) return null;

  return (
    <>
      <div className={styles.groupPhotos}>
        {profile.photos.map((photo) => {
          const baseUrl = photo.status_moder === 'CANCELED'
            ? photo.moderation_url || photo.url || '/images/photo-placeholder.svg'
            : photo.image_urls?.middle || photo.url || '/images/photo-placeholder.svg';
          const cacheBustedUrl = photo.updated_at
            ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(photo.updated_at)}`
            : baseUrl;
          const isLastActivePhoto = photo.status_moder === 'SUCCESS' && activePhotos.length <= 1;

          return (
            <PhotoCard
              key={photo.id}
              id={photo.id}
              src={cacheBustedUrl}
              isMain={photo.id === activeMainPhotoId}
              isActionsOpen={activePhotoId === photo.id}
              disabled={isAnyActionLoading}
              canDelete={!isLastActivePhoto}
              status={photo.status_moder}
              rejectReason={photo.reject_reason}
              onToggleActions={handleToggleActions}
              onRotate={() => handleRotate(photo.id)}
              onSetMain={() => handleSetMain(photo.id)}
              onDelete={() => setPhotoToDelete(photo.id)}
              onReplace={(file) => handleReplace(photo.id, file)}
            />
          );
        })}

        {uploadsToShow > 0 &&
          Array.from({ length: uploadsToShow }).map((_, i) => (
            <PhotoUpload key={i} />
          ))}
      </div>

      <p className={styles.photoHint}>
        Чтобы управлять фото, нажмите на него.
      </p>

      {fromShow === 'rega' && (
        <div className="btn-container mt-10 mb-5">
          <button
            type="button"
            className="btn-link"
            onClick={handleSuccessClick}
            disabled={isAnyActionLoading}
          >
            Продолжить
          </button>
        </div>
      )}

      {validationError && (
        <div className="error-box">
          {validationError}
        </div>
      )}

      <ConfirmModal
        isOpen={photoToDelete !== null}
        title="Удалить фото?"
        description="Фото будет удалено без возможности восстановления."
        isLoading={isDeleting}
        onCancel={() => setPhotoToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ProfilePhotos;
